import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { Terminal, X, Zap, Play, Code, FileCode2, Download, Eye, RefreshCw, Clock, Save, Star, Database, AlertTriangle, CheckCircle2, FileText, Copy, Upload, History, ArrowLeftRight, BookmarkIcon, ChevronDown, Info, RotateCcw, Layers, Globe, UserIcon, CalendarIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
// ─── Types ───────────────────────────────────────────────────────────────────
interface Template {
  id: string;
  name: string;
  version: string;
  updated: string;
  editor: string;
  status: 'active' | 'draft';
  prompt: string;
  structure: string[];
  params: string[];
}
interface TestHistoryItem {
  id: string;
  template: string;
  version: string;
  date: string;
  tokens: number;
  latency: string;
  cost: string;
  status: 'success' | 'error';
}
interface GenerationMetrics {
  model: string;
  tokens: number;
  latency: string;
  cost: string;
  promptTokens: number;
  completionTokens: number;
}
interface TestDataset {
  id: string;
  label: string;
  data: Record<string, unknown>;
}
type ViewMode = 'text' | 'json' | 'html' | 'pdf';
type PromptTab = 'system' | 'user' | 'final';
interface AISandboxModalProps {
  open: boolean;
  onClose: () => void;
  success: (msg: string) => void;
}
// ─── Mock Data ───────────────────────────────────────────────────────────────
const MOCK_TEMPLATES: Template[] = [{
  id: 'TPL-01',
  name: 'Натальная карта',
  version: 'v3.2',
  updated: '10.03.2026',
  editor: 'admin',
  status: 'active',
  prompt: 'You are an expert astrologer. Analyze the natal chart based on the provided planetary positions. Provide deep psychological insights, life purpose analysis, and practical recommendations. Structure the report with clear sections: Introduction, Personality Core, Emotional Landscape, Career Path, Relationships, Health, and Karmic Lessons.',
  structure: ['Intro', 'Анализ личности', 'Кармические задачи', 'Советы'],
  params: ['name', 'birthDate', 'birthTime', 'birthPlace']
}, {
  id: 'TPL-02',
  name: 'Соляр',
  version: 'v2.8',
  updated: '12.03.2026',
  editor: 'admin',
  status: 'active',
  prompt: 'Generate a solar return report focusing on the main themes of the upcoming year. Analyze the solar return chart in context of the natal chart. Cover: Main Year Theme, Career & Finance, Relationships & Love, Health & Wellbeing, Key Dates and Turning Points.',
  structure: ['Главная тема года', 'Финансы', 'Отношения', 'Здоровье'],
  params: ['name', 'birthDate', 'currentYear', 'location']
}, {
  id: 'TPL-03',
  name: 'Совместимость',
  version: 'v3.0',
  updated: '12.03.2026',
  editor: 'astro_maria',
  status: 'active',
  prompt: 'Analyze the synastry chart between Person A and Person B. Evaluate emotional compatibility, communication styles, conflict patterns, sexual chemistry, and long-term potential. Provide a compatibility score and practical advice.',
  structure: ['Общая совместимость', 'Эмоциональная связь', 'Конфликты', 'Перспективы'],
  params: ['personA_name', 'personA_birthDate', 'personA_birthTime', 'personA_birthPlace', 'personB_name', 'personB_birthDate', 'personB_birthTime', 'personB_birthPlace']
}, {
  id: 'TPL-04',
  name: 'Прогноз',
  version: 'v3.2',
  updated: '10.03.2026',
  editor: 'admin',
  status: 'active',
  prompt: 'Provide a transit forecast for the specified period. Analyze major planetary transits to natal positions. Highlight key dates, opportunities, and challenges.',
  structure: ['Общий фон', 'Ключевые даты', 'Предостережения'],
  params: ['name', 'birthDate', 'birthTime', 'birthPlace', 'period']
}, {
  id: 'TPL-05',
  name: 'Комбо',
  version: 'v1.0',
  updated: '14.03.2026',
  editor: 'dev_team',
  status: 'draft',
  prompt: 'Combine natal, solar, and transit analysis into one comprehensive report. Synthesize all data into actionable insights.',
  structure: ['Натал', 'Соляр', 'Транзиты', 'Синтез'],
  params: ['all_data']
}];
const MOCK_DATASETS: TestDataset[] = [{
  id: 'ds-natal',
  label: 'Натальная карта — тестовый',
  data: {
    name: 'Алексей Петров',
    birthDate: '1992-08-15',
    birthTime: '14:30',
    birthPlace: 'Москва, Россия'
  }
}, {
  id: 'ds-solar',
  label: 'Соляр — тестовый',
  data: {
    name: 'Мария Иванова',
    birthDate: '1988-03-22',
    currentYear: '2026',
    location: 'Санкт-Петербург, Россия'
  }
}, {
  id: 'ds-compat',
  label: 'Совместимость — тестовый',
  data: {
    personA_name: 'Анна',
    personA_birthDate: '1990-05-15',
    personA_birthTime: '10:00',
    personA_birthPlace: 'Москва',
    personB_name: 'Дмитрий',
    personB_birthDate: '1988-11-20',
    personB_birthTime: '16:45',
    personB_birthPlace: 'Казань'
  }
}, {
  id: 'ds-forecast',
  label: 'Прогноз — тестовый',
  data: {
    name: 'Елена Сидорова',
    birthDate: '1995-01-10',
    birthTime: '08:15',
    birthPlace: 'Новосибирск',
    period: '2026-03-01 — 2026-03-31'
  }
}];
const MOCK_HISTORY: TestHistoryItem[] = [{
  id: 'TH-1',
  template: 'Соляр',
  version: 'v2.8',
  date: '15.03 14:20',
  tokens: 3450,
  latency: '42 сек',
  cost: '$0.09',
  status: 'success'
}, {
  id: 'TH-2',
  template: 'Натальная карта',
  version: 'v3.1',
  date: '15.03 13:05',
  tokens: 5200,
  latency: '58 сек',
  cost: '$0.14',
  status: 'success'
}, {
  id: 'TH-3',
  template: 'Совместимость',
  version: 'v3.0',
  date: '15.03 11:30',
  tokens: 4100,
  latency: '51 сек',
  cost: '$0.11',
  status: 'success'
}, {
  id: 'TH-4',
  template: 'Прогноз',
  version: 'v3.2',
  date: '14.03 17:40',
  tokens: 0,
  latency: '—',
  cost: '$0.00',
  status: 'error'
}, {
  id: 'TH-5',
  template: 'Соляр',
  version: 'v2.7',
  date: '14.03 15:10',
  tokens: 3100,
  latency: '38 сек',
  cost: '$0.08',
  status: 'success'
}];
const MOCK_GENERATED_TEXT = `# Отчёт «Соляр» — Мария Иванова

## Главная тема года (2026)

Солнечное возвращение 2026 года формирует мощную конфигурацию в вашей карте. Солнце в соляре попадает в 10-й дом, что делает этот год ключевым для карьерного роста и публичного признания.

Юпитер в тригоне к натальному Солнцу открывает двери для расширения профессиональных горизонтов. Это год, когда ваши усилия последних лет начинают приносить видимые плоды.

## Финансы

Венера в 2-м доме соляра указывает на стабилизацию финансового потока. Ожидайте увеличения дохода во второй половине года, особенно в период сентябрь–ноябрь, когда транзитный Юпитер активирует вашу натальную Венеру.

**Рекомендация:** Избегайте крупных инвестиций в период ретроградного Меркурия (апрель, август, декабрь).

## Отношения

Марс в 7-м доме соляра привносит динамику в партнёрские отношения. Возможны как конфликты, так и страстное обновление связи. Ключевой период — май–июнь.

Для одиноких: высокая вероятность значимой встречи в период октябрь–ноябрь.

## Здоровье

Сатурн в 6-м доме соляра требует дисциплины в вопросах здоровья. Обратите внимание на опорно-двигательный аппарат и суставы. Регулярные физические нагрузки и правильное питание — ваши главные союзники в этом году.

---
*Отчёт сгенерирован AI-системой AstroAI v2.8*`;
const MOCK_GENERATED_JSON = {
  report: {
    type: 'solar_return',
    version: '2.8',
    subject: 'Мария Иванова',
    year: 2026,
    sections: [{
      title: 'Главная тема года',
      score: 8.5,
      keywords: ['карьера', 'признание', '10-й дом']
    }, {
      title: 'Финансы',
      score: 7.2,
      keywords: ['стабилизация', 'Венера', '2-й дом']
    }, {
      title: 'Отношения',
      score: 6.8,
      keywords: ['динамика', 'Марс', '7-й дом']
    }, {
      title: 'Здоровье',
      score: 6.0,
      keywords: ['дисциплина', 'Сатурн', '6-й дом']
    }],
    overallScore: 7.1
  }
};
const MOCK_GENERATED_HTML = `<div style="font-family: Georgia, serif; max-width: 700px; margin: 0 auto; padding: 32px; color: #1e293b;">
  <h1 style="font-size: 24px; border-bottom: 2px solid #6366f1; padding-bottom: 12px; color: #1e1b4b;">Отчёт «Соляр» — Мария Иванова</h1>
  <h2 style="color: #4338ca; margin-top: 24px;">Главная тема года (2026)</h2>
  <p>Солнечное возвращение 2026 года формирует мощную конфигурацию. Солнце попадает в 10-й дом — год карьерного роста.</p>
  <h2 style="color: #4338ca; margin-top: 24px;">Финансы</h2>
  <p>Венера в 2-м доме соляра указывает на стабилизацию финансового потока.</p>
  <h2 style="color: #4338ca; margin-top: 24px;">Отношения</h2>
  <p>Марс в 7-м доме привносит динамику в партнёрские отношения.</p>
</div>`;
// ─── Validation helpers ──────────────────────────────────────────────────────
interface ValidationError {
  field: string;
  message: string;
}
function validateInputData(jsonStr: string, requiredParams: string[]): {
  valid: boolean;
  errors: ValidationError[];
  parsed: Record<string, unknown> | null;
} {
  const errors: ValidationError[] = [];
  let parsed: Record<string, unknown> | null = null;
  try {
    parsed = JSON.parse(jsonStr);
  } catch {
    return {
      valid: false,
      errors: [{
        field: 'json',
        message: 'Невалидный JSON. Проверьте синтаксис.'
      }],
      parsed: null
    };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return {
      valid: false,
      errors: [{
        field: 'json',
        message: 'JSON должен быть объектом {}'
      }],
      parsed: null
    };
  }
  for (const param of requiredParams) {
    if (!(param in parsed) || parsed[param] === '' || parsed[param] === null || parsed[param] === undefined) {
      errors.push({
        field: param,
        message: `Обязательное поле «${param}» не заполнено`
      });
    }
  }
  // Date validation
  const dateFields = Object.keys(parsed).filter(k => k.toLowerCase().includes('date') || k.toLowerCase().includes('birth'));
  for (const df of dateFields) {
    const val = parsed[df];
    if (typeof val === 'string' && val.trim()) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(val)) {
        errors.push({
          field: df,
          message: `Поле «${df}»: формат даты должен быть YYYY-MM-DD`
        });
      } else {
        const d = new Date(val);
        if (isNaN(d.getTime())) {
          errors.push({
            field: df,
            message: `Поле «${df}»: некорректная дата`
          });
        }
      }
    }
  }
  // Time validation
  const timeFields = Object.keys(parsed).filter(k => k.toLowerCase().includes('time'));
  for (const tf of timeFields) {
    const val = parsed[tf];
    if (typeof val === 'string' && val.trim()) {
      const timeRegex = /^\d{2}:\d{2}$/;
      if (!timeRegex.test(val)) {
        errors.push({
          field: tf,
          message: `Поле «${tf}»: формат времени должен быть HH:MM`
        });
      } else {
        const [h, m] = val.split(':').map(Number);
        if (h < 0 || h > 23 || m < 0 || m > 59) {
          errors.push({
            field: tf,
            message: `Поле «${tf}»: некорректное время`
          });
        }
      }
    }
  }
  return {
    valid: errors.length === 0,
    errors,
    parsed
  };
}
// ─── Rate Limiter ────────────────────────────────────────────────────────────
function useRateLimiter(maxPerMinute: number) {
  const [timestamps, setTimestamps] = useState<number[]>([]);
  const [remaining, setRemaining] = useState(maxPerMinute);
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const recent = timestamps.filter(t => now - t < 60000);
      setTimestamps(recent);
      setRemaining(maxPerMinute - recent.length);
    }, 1000);
    return () => clearInterval(interval);
  }, [timestamps, maxPerMinute]);
  const canGenerate = remaining > 0;
  const recordGeneration = () => {
    setTimestamps(prev => [...prev, Date.now()]);
    setRemaining(prev => prev - 1);
  };
  return {
    canGenerate,
    remaining,
    recordGeneration
  };
}
// ─── Component ───────────────────────────────────────────────────────────────
export function AISandboxModal({
  open,
  onClose,
  success
}: AISandboxModalProps) {
  // Template state
  const [selectedTemplateId, setSelectedTemplateId] = useState(MOCK_TEMPLATES[1].id);
  const selectedTemplate = MOCK_TEMPLATES.find(t => t.id === selectedTemplateId) || MOCK_TEMPLATES[0];
  // Input state
  const [inputJson, setInputJson] = useState(JSON.stringify(MOCK_DATASETS[1].data, null, 2));
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [showDatasetDropdown, setShowDatasetDropdown] = useState(false);
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasResult, setHasResult] = useState(false);
  const [generationMetrics, setGenerationMetrics] = useState<GenerationMetrics | null>(null);
  // View mode
  const [viewMode, setViewMode] = useState<ViewMode>('text');
  // Prompt log
  const [showPromptLog, setShowPromptLog] = useState(false);
  const [promptTab, setPromptTab] = useState<PromptTab>('system');
  // History
  const [showHistory, setShowHistory] = useState(false);
  // Compare
  const [showCompare, setShowCompare] = useState(false);
  const [compareVersionA, setCompareVersionA] = useState('v2.8');
  const [compareVersionB, setCompareVersionB] = useState('v2.7');
  // Rate limiter
  const {
    canGenerate,
    remaining,
    recordGeneration
  } = useRateLimiter(5);
  // Refs
  const outputRef = useRef<HTMLDivElement>(null);
  const datasetRef = useRef<HTMLDivElement>(null);
  // Close dataset dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (datasetRef.current && !datasetRef.current.contains(e.target as Node)) {
        setShowDatasetDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  // Handle template change
  const handleTemplateChange = (id: string) => {
    setSelectedTemplateId(id);
    const tpl = MOCK_TEMPLATES.find(t => t.id === id);
    if (tpl) {
      const dataset = MOCK_DATASETS.find(d => d.label.toLowerCase().includes(tpl.name.toLowerCase().split(' ')[0]));
      if (dataset) {
        setInputJson(JSON.stringify(dataset.data, null, 2));
      }
    }
    setHasResult(false);
    setGenerationMetrics(null);
    setValidationErrors([]);
  };
  // Load dataset
  const handleLoadDataset = (dataset: TestDataset) => {
    setInputJson(JSON.stringify(dataset.data, null, 2));
    setShowDatasetDropdown(false);
    setValidationErrors([]);
    success(`Набор «${dataset.label}» загружен`);
  };
  // Validate & Generate
  const handleGenerate = useCallback(() => {
    const {
      valid,
      errors
    } = validateInputData(inputJson, selectedTemplate.params);
    setValidationErrors(errors);
    if (!valid) return;
    if (!canGenerate) {
      setValidationErrors([{
        field: 'rate',
        message: `Лимит: максимум 5 тестов/мин. Осталось: ${remaining}`
      }]);
      return;
    }
    setIsGenerating(true);
    setHasResult(false);
    setGenerationMetrics(null);
    recordGeneration();
    // Simulate generation
    setTimeout(() => {
      setIsGenerating(false);
      setHasResult(true);
      setGenerationMetrics({
        model: 'GPT-5',
        tokens: 3450,
        latency: '42 сек',
        cost: '$0.09',
        promptTokens: 820,
        completionTokens: 2630
      });
      success('Генерация завершена');
    }, 2500);
  }, [inputJson, selectedTemplate, canGenerate, remaining, recordGeneration, success]);
  // Copy output
  const handleCopy = () => {
    let content = MOCK_GENERATED_TEXT;
    if (viewMode === 'json') content = JSON.stringify(MOCK_GENERATED_JSON, null, 2);
    if (viewMode === 'html') content = MOCK_GENERATED_HTML;
    navigator.clipboard.writeText(content);
    success('Результат скопирован в буфер обмена');
  };
  // Download PDF (mock)
  const handleDownloadPdf = () => {
    success('Скачивание PDF...');
  };
  // Use as template
  const handleUseAsTemplate = () => {
    success('Результат сохранён как шаблон-пример');
  };
  // Load from history
  const handleLoadFromHistory = (item: TestHistoryItem) => {
    const tpl = MOCK_TEMPLATES.find(t => t.name === item.template);
    if (tpl) {
      setSelectedTemplateId(tpl.id);
      const dataset = MOCK_DATASETS.find(d => d.label.toLowerCase().includes(tpl.name.toLowerCase().split(' ')[0]));
      if (dataset) setInputJson(JSON.stringify(dataset.data, null, 2));
    }
    setShowHistory(false);
    success(`Тест «${item.template} ${item.version}» загружен`);
  };
  if (!open) return null;
  // ─── Render helpers ──────────────────────────────────────────────────────
  const renderViewModeContent = () => {
    if (!hasResult) {
      return <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3 py-12" data-id="element-9390">
          <Terminal className="w-10 h-10 opacity-30" data-id="element-9391" />
          <p className="text-sm italic" data-id="element-9392">
            Нажмите «Сгенерировать» для получения результата...
          </p>
        </div>;
    }
    if (isGenerating) {
      return <div className="flex-1 flex flex-col items-center justify-center gap-3 py-12" data-id="element-9393">
          <div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" data-id="element-9394" />
          <p className="text-sm text-slate-500" data-id="element-9395">Генерация отчёта...</p>
        </div>;
    }
    switch (viewMode) {
      case 'text':
        return <div className="flex-1 overflow-y-auto p-4 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap font-sans" data-id="element-9396">
            {MOCK_GENERATED_TEXT}
          </div>;
      case 'json':
        return <div className="flex-1 overflow-y-auto" data-id="element-9397">
            <pre className="p-4 text-xs font-mono text-emerald-400 bg-slate-900 rounded-lg m-2 overflow-x-auto leading-relaxed" data-id="element-9398">
              {JSON.stringify(MOCK_GENERATED_JSON, null, 2)}
            </pre>
          </div>;
      case 'html':
        return <div className="flex-1 overflow-y-auto p-4" data-id="element-9399">
            <div className="border border-slate-200 rounded-lg bg-white p-4 shadow-inner" dangerouslySetInnerHTML={{
            __html: MOCK_GENERATED_HTML
          }} data-id="element-9400" />
          </div>;
      case 'pdf':
        return <div className="flex-1 overflow-y-auto p-4" data-id="element-9401">
            <div className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 p-8 flex flex-col items-center justify-center gap-3 min-h-[300px]" data-id="element-9402">
              <FileText className="w-12 h-12 text-slate-300" data-id="element-9403" />
              <p className="text-sm font-medium text-slate-500" data-id="element-9404">PDF Preview</p>
              <p className="text-xs text-slate-400 text-center max-w-xs" data-id="element-9405">
                Предварительный просмотр PDF. Для полной версии нажмите «Скачать
                PDF».
              </p>
              <Button variant="outline" size="sm" icon={<Download className="w-3.5 h-3.5" data-id="element-9407" />} onClick={handleDownloadPdf} data-id="element-9406">
                Скачать PDF
              </Button>
            </div>
          </div>;
    }
  };
  const renderPromptLog = () => {
    const systemPrompt = selectedTemplate.prompt;
    const userPrompt = `Сгенерируй отчёт типа "${selectedTemplate.name}" для следующих данных:\n${inputJson}`;
    const finalPrompt = `[SYSTEM]\n${systemPrompt}\n\n[USER]\n${userPrompt}\n\n[PARAMETERS]\nmodel: GPT-5\ntemperature: 0.7\nmax_tokens: 8192\ntop_p: 1.0`;
    const prompts: Record<PromptTab, {
      label: string;
      content: string;
    }> = {
      system: {
        label: 'System Prompt',
        content: systemPrompt
      },
      user: {
        label: 'User Prompt',
        content: userPrompt
      },
      final: {
        label: 'Final Prompt',
        content: finalPrompt
      }
    };
    return <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-3" data-id="element-9408">
        <div className="flex items-center justify-between" data-id="element-9409">
          <div className="flex items-center gap-2" data-id="element-9410">
            <Code className="w-4 h-4 text-indigo-600" data-id="element-9411" />
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider" data-id="element-9412">
              Prompt Log
            </span>
          </div>
          <button onClick={() => setShowPromptLog(false)} className="text-slate-400 hover:text-slate-600" data-id="element-9413">
            <X className="w-4 h-4" data-id="element-9414" />
          </button>
        </div>
        <div className="flex gap-1" data-id="element-9415">
          {(Object.keys(prompts) as PromptTab[]).map(tab => <button key={tab} onClick={() => setPromptTab(tab)} className={`px-3 py-1.5 text-[11px] font-medium rounded-lg transition-colors ${promptTab === tab ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'}`} data-id="element-9416">
              {prompts[tab].label}
            </button>)}
        </div>
        <pre className="bg-slate-900 text-emerald-400 p-3 rounded-lg text-[11px] font-mono max-h-48 overflow-y-auto leading-relaxed whitespace-pre-wrap" data-id="element-9417">
          {prompts[promptTab].content}
        </pre>
      </div>;
  };
  const renderComparePanel = () => {
    const diffLines = [{
      type: 'context',
      text: '  Generate a solar return report focusing on'
    }, {
      type: 'removed',
      text: '- the main themes and general overview of the year.'
    }, {
      type: 'added',
      text: '+ the main themes of the upcoming year. Analyze the'
    }, {
      type: 'added',
      text: '+ solar return chart in context of the natal chart.'
    }, {
      type: 'context',
      text: '  Cover: Main Year Theme, Career & Finance,'
    }, {
      type: 'removed',
      text: '- Relationships, Health.'
    }, {
      type: 'added',
      text: '+ Relationships & Love, Health & Wellbeing,'
    }, {
      type: 'added',
      text: '+ Key Dates and Turning Points.'
    }];
    return <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-3" data-id="element-9418">
        <div className="flex items-center justify-between" data-id="element-9419">
          <div className="flex items-center gap-2" data-id="element-9420">
            <ArrowLeftRight className="w-4 h-4 text-indigo-600" data-id="element-9421" />
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider" data-id="element-9422">
              Сравнение версий
            </span>
          </div>
          <button onClick={() => setShowCompare(false)} className="text-slate-400 hover:text-slate-600" data-id="element-9423">
            <X className="w-4 h-4" data-id="element-9424" />
          </button>
        </div>
        <div className="flex items-center gap-3" data-id="element-9425">
          <select className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white" value={compareVersionA} onChange={e => setCompareVersionA(e.target.value)} data-id="element-9426">
            <option value="v2.7" data-id="element-9427">Соляр v2.7</option>
            <option value="v2.8" data-id="element-9428">Соляр v2.8</option>
          </select>
          <span className="text-xs text-slate-400 font-medium" data-id="element-9429">vs</span>
          <select className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white" value={compareVersionB} onChange={e => setCompareVersionB(e.target.value)} data-id="element-9430">
            <option value="v2.7" data-id="element-9431">Соляр v2.7</option>
            <option value="v2.8" data-id="element-9432">Соляр v2.8</option>
          </select>
        </div>
        <div className="bg-slate-900 rounded-lg p-3 font-mono text-[11px] max-h-48 overflow-y-auto leading-relaxed" data-id="element-9433">
          {diffLines.map((line, i) => <div key={i} className={line.type === 'removed' ? 'text-rose-400 bg-rose-950/30' : line.type === 'added' ? 'text-emerald-400 bg-emerald-950/30' : 'text-slate-500'} data-id="element-9434">
              {line.text}
            </div>)}
        </div>
      </div>;
  };
  const renderHistoryPanel = () => <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-3" data-id="element-9435">
      <div className="flex items-center justify-between" data-id="element-9436">
        <div className="flex items-center gap-2" data-id="element-9437">
          <History className="w-4 h-4 text-indigo-600" data-id="element-9438" />
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wider" data-id="element-9439">
            Последние тесты
          </span>
        </div>
        <button onClick={() => setShowHistory(false)} className="text-slate-400 hover:text-slate-600" data-id="element-9440">
          <X className="w-4 h-4" data-id="element-9441" />
        </button>
      </div>
      <div className="space-y-1.5 max-h-48 overflow-y-auto" data-id="element-9442">
        {MOCK_HISTORY.map(item => <button key={item.id} onClick={() => handleLoadFromHistory(item)} className="w-full flex items-center justify-between p-2.5 bg-white border border-slate-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors text-left group" data-id="element-9443">
            <div className="flex items-center gap-3" data-id="element-9444">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.status === 'success' ? 'bg-emerald-500' : 'bg-rose-500'}`} data-id="element-9445" />
              <div data-id="element-9446">
                <div className="text-xs font-medium text-slate-800 group-hover:text-indigo-700" data-id="element-9447">
                  {item.template}{' '}
                  <span className="text-slate-400" data-id="element-9448">{item.version}</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5" data-id="element-9449">
                  {item.date} ·{' '}
                  {item.tokens > 0 ? `${item.tokens} tok` : 'ошибка'} ·{' '}
                  {item.cost}
                </div>
              </div>
            </div>
            <RotateCcw className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-500 flex-shrink-0" data-id="element-9450" />
          </button>)}
      </div>
    </div>;
  // ─── Main Render ─────────────────────────────────────────────────────────
  return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-9451">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95" data-id="element-9452">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex-shrink-0" data-id="element-9453">
          <div className="flex items-center gap-3" data-id="element-9454">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center" data-id="element-9455">
              <Terminal className="w-4.5 h-4.5 text-indigo-600" data-id="element-9456" />
            </div>
            <div data-id="element-9457">
              <h3 className="font-bold text-slate-900 text-sm" data-id="element-9458">
                AI Тест-песочница
              </h3>
              <p className="text-[10px] text-slate-400 mt-0.5" data-id="element-9459">
                Тестирование шаблонов отчётов без создания реального заказа
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-9460">
            {/* Rate limit indicator */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-100 rounded-lg" data-id="element-9461">
              <AlertTriangle className={`w-3.5 h-3.5 ${remaining <= 1 ? 'text-rose-500' : remaining <= 3 ? 'text-amber-500' : 'text-slate-400'}`} data-id="element-9462" />
              <span className={`text-[10px] font-bold ${remaining <= 1 ? 'text-rose-600' : remaining <= 3 ? 'text-amber-600' : 'text-slate-500'}`} data-id="element-9463">
                {remaining}/5 мин
              </span>
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors" data-id="element-9464">
              <X className="w-5 h-5" data-id="element-9465" />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto" data-id="element-9466">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x divide-slate-200 min-h-[500px]" data-id="element-9467">
            {/* ── LEFT: Input Panel ── */}
            <div className="flex flex-col p-5 space-y-4 overflow-y-auto" data-id="element-9468">
              {/* Template selector with metadata */}
              <div data-id="element-9469">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-9470">
                  Шаблон
                </label>
                <select className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow" value={selectedTemplateId} onChange={e => handleTemplateChange(e.target.value)} data-id="element-9471">
                  {MOCK_TEMPLATES.map(t => <option key={t.id} value={t.id} data-id="element-9472">
                      {t.name} {t.version}
                    </option>)}
                </select>
                {/* Template metadata */}
                <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-9473">
                  <Badge variant="outline" size="sm" className="text-[10px] gap-1" data-id="element-9474">
                    <Layers className="w-3 h-3" data-id="element-9475" />
                    {selectedTemplate.version}
                  </Badge>
                  <Badge variant="neutral" size="sm" className="text-[10px] gap-1" data-id="element-9476">
                    <CalendarIcon className="w-3 h-3" data-id="element-9477" />
                    обновлён: {selectedTemplate.updated}
                  </Badge>
                  <Badge variant="neutral" size="sm" className="text-[10px] gap-1" data-id="element-9478">
                    <UserIcon className="w-3 h-3" data-id="element-9479" />
                    {selectedTemplate.editor}
                  </Badge>
                  <Badge variant={selectedTemplate.status === 'active' ? 'success' : 'warning'} size="sm" className="text-[10px]" data-id="element-9480">
                    {selectedTemplate.status === 'active' ? 'Активен' : 'Черновик'}
                  </Badge>
                </div>
              </div>

              {/* Input JSON */}
              <div className="flex-1 flex flex-col min-h-0" data-id="element-9481">
                <div className="flex items-center justify-between mb-2" data-id="element-9482">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-9483">
                    Входные данные (JSON)
                  </label>
                  <div className="relative" ref={datasetRef} data-id="element-9484">
                    <Button variant="ghost" size="sm" className="text-[11px] h-7 px-2.5 gap-1" icon={<Upload className="w-3 h-3" data-id="element-9486" />} onClick={() => setShowDatasetDropdown(!showDatasetDropdown)} data-id="element-9485">
                      Тестовый набор
                      <ChevronDown className="w-3 h-3" data-id="element-9487" />
                    </Button>
                    {showDatasetDropdown && <div className="absolute right-0 top-8 w-64 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95" data-id="element-9488">
                        <div className="px-3 py-2 border-b border-slate-100" data-id="element-9489">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-9490">
                            Загрузить тестовый набор
                          </span>
                        </div>
                        {MOCK_DATASETS.map(ds => <button key={ds.id} className="w-full text-left px-3 py-2.5 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors" onClick={() => handleLoadDataset(ds)} data-id="element-9491">
                            <Database className="w-3.5 h-3.5 text-slate-400" data-id="element-9492" />
                            {ds.label}
                          </button>)}
                      </div>}
                  </div>
                </div>

                {/* Required params hint */}
                <div className="flex flex-wrap gap-1 mb-2" data-id="element-9493">
                  {selectedTemplate.params.map(p => <span key={p} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[9px] font-mono" data-id="element-9494">
                      {p}
                    </span>)}
                </div>

                <textarea className="flex-1 min-h-[200px] w-full p-3 font-mono text-xs bg-slate-900 text-emerald-400 rounded-lg border-0 focus:ring-2 focus:ring-indigo-500 resize-none leading-relaxed" value={inputJson} onChange={e => {
                setInputJson(e.target.value);
                setValidationErrors([]);
              }} spellCheck={false} data-id="element-9495" />

                {/* Validation errors */}
                {validationErrors.length > 0 && <div className="mt-2 space-y-1" data-id="element-9496">
                    {validationErrors.map((err, i) => <div key={i} className="flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg" data-id="element-9497">
                        <AlertTriangle className="w-3.5 h-3.5 text-rose-500 flex-shrink-0 mt-0.5" data-id="element-9498" />
                        <span className="text-[11px] text-rose-700" data-id="element-9499">
                          {err.message}
                        </span>
                      </div>)}
                  </div>}
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-2 flex-shrink-0" data-id="element-9500">
                <Button variant="primary" className="w-full" icon={isGenerating ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-9502" /> : <Zap className="w-4 h-4" data-id="element-9503" />} onClick={handleGenerate} disabled={isGenerating || !canGenerate} data-id="element-9501">
                  {isGenerating ? 'Генерация...' : 'Сгенерировать'}
                </Button>
                <div className="flex gap-2" data-id="element-9504">
                  <Button variant="ghost" size="sm" className="flex-1 text-[11px]" icon={<History className="w-3.5 h-3.5" data-id="element-9506" />} onClick={() => {
                  setShowHistory(!showHistory);
                  setShowPromptLog(false);
                  setShowCompare(false);
                }} data-id="element-9505">
                    История
                  </Button>
                  <Button variant="ghost" size="sm" className="flex-1 text-[11px]" icon={<Code className="w-3.5 h-3.5" data-id="element-9508" />} onClick={() => {
                  setShowPromptLog(!showPromptLog);
                  setShowHistory(false);
                  setShowCompare(false);
                }} data-id="element-9507">
                    Prompt
                  </Button>
                  <Button variant="ghost" size="sm" className="flex-1 text-[11px]" icon={<ArrowLeftRight className="w-3.5 h-3.5" data-id="element-9510" />} onClick={() => {
                  setShowCompare(!showCompare);
                  setShowHistory(false);
                  setShowPromptLog(false);
                }} data-id="element-9509">
                    Сравнить
                  </Button>
                </div>
              </div>
            </div>

            {/* ── RIGHT: Output Panel ── */}
            <div className="flex flex-col overflow-hidden" data-id="element-9511">
              {/* Output header with view modes */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50/50 flex-shrink-0" data-id="element-9512">
                <div className="flex items-center gap-2" data-id="element-9513">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-9514">
                    Результат
                  </label>
                </div>
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-id="element-9515">
                  {[{
                  id: 'text' as ViewMode,
                  label: 'TEXT',
                  icon: FileText
                }, {
                  id: 'json' as ViewMode,
                  label: 'JSON',
                  icon: Code
                }, {
                  id: 'html' as ViewMode,
                  label: 'HTML',
                  icon: Globe
                }, {
                  id: 'pdf' as ViewMode,
                  label: 'PDF',
                  icon: Eye
                }].map(mode => <button key={mode.id} onClick={() => setViewMode(mode.id)} className={`flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold rounded-md transition-all ${viewMode === mode.id ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-9516">
                      <mode.icon className="w-3 h-3" data-id="element-9517" />
                      {mode.label}
                    </button>)}
                </div>
              </div>

              {/* Generation metrics */}
              {generationMetrics && hasResult && <div className="px-5 py-2.5 border-b border-slate-200 bg-emerald-50/50 flex-shrink-0" data-id="element-9518">
                  <div className="flex flex-wrap items-center gap-3" data-id="element-9519">
                    <div className="flex items-center gap-1.5" data-id="element-9520">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-9521" />
                      <span className="text-[10px] text-slate-500" data-id="element-9522">Model:</span>
                      <span className="text-[10px] font-bold text-slate-800" data-id="element-9523">
                        {generationMetrics.model}
                      </span>
                    </div>
                    <div className="w-px h-3 bg-slate-200" data-id="element-9524" />
                    <div className="flex items-center gap-1.5" data-id="element-9525">
                      <span className="text-[10px] text-slate-500" data-id="element-9526">
                        Tokens:
                      </span>
                      <span className="text-[10px] font-bold text-slate-800" data-id="element-9527">
                        {generationMetrics.tokens.toLocaleString()}
                      </span>
                      <span className="text-[9px] text-slate-400" data-id="element-9528">
                        ({generationMetrics.promptTokens}+
                        {generationMetrics.completionTokens})
                      </span>
                    </div>
                    <div className="w-px h-3 bg-slate-200" data-id="element-9529" />
                    <div className="flex items-center gap-1.5" data-id="element-9530">
                      <Clock className="w-3 h-3 text-slate-400" data-id="element-9531" />
                      <span className="text-[10px] font-bold text-slate-800" data-id="element-9532">
                        {generationMetrics.latency}
                      </span>
                    </div>
                    <div className="w-px h-3 bg-slate-200" data-id="element-9533" />
                    <div className="flex items-center gap-1.5" data-id="element-9534">
                      <span className="text-[10px] text-slate-500" data-id="element-9535">Cost:</span>
                      <span className="text-[10px] font-bold text-emerald-700" data-id="element-9536">
                        {generationMetrics.cost}
                      </span>
                    </div>
                  </div>
                </div>}

              {/* Loading state */}
              {isGenerating && <div className="flex-1 flex flex-col items-center justify-center gap-3 py-16" data-id="element-9537">
                  <div className="w-10 h-10 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" style={{
                borderWidth: '3px'
              }} data-id="element-9538" />
                  <p className="text-sm text-slate-500 font-medium" data-id="element-9539">
                    Генерация отчёта...
                  </p>
                  <p className="text-[10px] text-slate-400" data-id="element-9540">
                    Обычно занимает 30–60 секунд
                  </p>
                </div>}

              {/* Output content */}
              {!isGenerating && <div ref={outputRef} className="flex-1 overflow-y-auto" data-id="element-9541">
                  {renderViewModeContent()}
                </div>}

              {/* Output action bar */}
              {hasResult && !isGenerating && <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50/50 flex-shrink-0" data-id="element-9542">
                  <div className="flex gap-2" data-id="element-9543">
                    <Button variant="outline" size="sm" className="text-[11px]" icon={<Copy className="w-3.5 h-3.5" data-id="element-9545" />} onClick={handleCopy} data-id="element-9544">
                      Копировать
                    </Button>
                    <Button variant="outline" size="sm" className="text-[11px]" icon={<Download className="w-3.5 h-3.5" data-id="element-9547" />} onClick={handleDownloadPdf} data-id="element-9546">
                      Скачать PDF
                    </Button>
                  </div>
                  <Button variant="ghost" size="sm" className="text-[11px]" icon={<BookmarkIcon className="w-3.5 h-3.5" data-id="element-9549" />} onClick={handleUseAsTemplate} data-id="element-9548">
                    Сохранить как пример
                  </Button>
                </div>}
            </div>
          </div>

          {/* ── Bottom Panels (Prompt Log / History / Compare) ── */}
          {showPromptLog && renderPromptLog()}
          {showHistory && renderHistoryPanel()}
          {showCompare && renderComparePanel()}
        </div>
      </div>
    </div>;
}